import {
  Building2,
  LayoutDashboard,
  LogOut,
  Moon,
  ReceiptText,
  Sun,
  Users,
  Webhook,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useTheme } from 'next-themes';
import { useEffect, useState, type ComponentType } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { ModeSwitcher } from '@/components/ModeSwitcher';
import { RouteTransition } from '@/components/RouteTransition';
import { SandboxBanner } from '@/components/SandboxBanner';
import { VersionBadge } from '@/components/VersionBadge';
import { usePortalAuth } from '@/lib/portal-auth/usePortalAuth';
import { ClientUserRoleRank, type ClientUserRole } from '@/lib/portal-auth/types';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  minRole: ClientUserRole;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/portal',              label: 'Dashboard',    minRole: 'ClientViewer', icon: LayoutDashboard, end: true },
      { to: '/portal/merchants',    label: 'Merchants',    minRole: 'ClientViewer', icon: Building2 },
      { to: '/portal/transactions', label: 'Transactions', minRole: 'ClientViewer', icon: ReceiptText },
      { to: '/portal/webhooks',     label: 'Webhooks',     minRole: 'ClientViewer', icon: Webhook },
    ],
  },
  {
    label: 'Team',
    items: [
      { to: '/portal/users', label: 'Users', minRole: 'ClientAdmin', icon: Users },
    ],
  },
];

export function PortalLayout() {
  const { user, logout } = usePortalAuth();
  const navigate = useNavigate();

  const visibleSections = user
    ? NAV_SECTIONS
        .map((s) => ({
          ...s,
          items: s.items.filter((n) => ClientUserRoleRank[user.role] >= ClientUserRoleRank[n.minRole]),
        }))
        .filter((s) => s.items.length > 0)
    : [];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Sandbox chrome — rendered only in sandbox mode. See AppLayout. */}
      <SandboxBanner />
      <div className="grid flex-1 grid-cols-[284px_1fr]">
      {/*
        Sticky sidebar: pinned to the top of the viewport at full screen
        height so the nav stays visible while the right column scrolls past.
        The inner <nav> already has overflow-y-auto so long nav lists scroll
        within the rail rather than growing the sidebar beyond the viewport.
      */}
      <aside className="sticky top-0 flex h-screen flex-col overflow-hidden border-r border-border/60 bg-card/40 backdrop-blur-sm">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,theme(colors.primary/10%),transparent_55%)]"
        />

        <div className="relative px-6 pb-5 pt-6">
          <Logo className="h-8" />
          <div className="mt-3">
            <ModeSwitcher subtitle="Partner portal" />
          </div>
        </div>

        <nav className="relative flex-1 space-y-6 overflow-y-auto px-3 pb-4 pt-2 text-sm">
          {visibleSections.map((section) => (
            <SidebarSection key={section.label} section={section} />
          ))}
        </nav>

        {user && (
          <div className="relative border-t border-border/50 p-3">
            <UserCard
              email={user.email}
              fullName={user.fullName}
              role={user.role}
              onSignOut={async () => {
                await logout();
                navigate('/portal/login', { replace: true });
              }}
            />
          </div>
        )}

        <VersionBadge />
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-10 flex h-14 items-center justify-end gap-2 border-b border-border/60 bg-background/80 px-6 backdrop-blur-sm">
          <ThemeToggle />
        </header>
        <main className="flex-1 overflow-y-auto p-8">
          <RouteTransition />
        </main>
      </div>
      </div>
    </div>
  );
}

function SidebarSection({ section }: { section: NavSection }) {
  return (
    <div>
      <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/60">
        {section.label}
      </div>
      <div className="space-y-0.5">
        {section.items.map((item) => (
          <SidebarNavLink key={item.to} item={item} />
        ))}
      </div>
    </div>
  );
}

function SidebarNavLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  const reduceMotion = useReducedMotion();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg py-2 pl-2.5 pr-3 font-medium transition-colors duration-150',
          isActive
            ? 'bg-accent text-foreground'
            : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <motion.span
              aria-hidden
              layoutId="portal-sidebar-rail"
              data-testid="portal-sidebar-rail"
              className="absolute inset-y-1.5 left-0 w-0.5 rounded-r-full bg-primary"
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { type: 'spring', stiffness: 500, damping: 40 }
              }
            />
          )}
          <span
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150',
              isActive
                ? 'bg-primary/10 text-primary'
                : 'bg-transparent text-muted-foreground/80 group-hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
          </span>
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

interface UserCardProps {
  email: string;
  fullName: string | null;
  role: ClientUserRole;
  onSignOut: () => void;
}

function UserCard({ email, fullName, role, onSignOut }: UserCardProps) {
  const initials = (fullName || email)
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('') || 'U';

  // Name/avatar block is the profile entry point. Mirrors the admin layout's
  // UserCard pattern so the affordance is consistent across surfaces.
  return (
    <div className="flex items-center gap-3 rounded-xl bg-background/60 p-2.5 shadow-sm ring-1 ring-border/40">
      <Link
        to="/portal/profile"
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg outline-offset-2 transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        title="Edit profile"
      >
        <span
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary/70 text-xs font-semibold tracking-wide text-primary-foreground shadow-inner"
        >
          {initials}
        </span>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-xs font-semibold text-foreground">{fullName || email}</div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <span
              aria-hidden
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                role === 'ClientAdmin' ? 'bg-emerald-400' : 'bg-sky-400',
              )}
            />
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
              {role}
            </span>
          </div>
        </div>
      </Link>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
        onClick={onSignOut}
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut className="h-4 w-4" />
      </Button>
    </div>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;

  const isDark = theme === 'dark';
  return (
    <Button
      variant="ghost"
      size="icon"
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
}
